import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { AttentionItem } from "@wms/domain";
import { Alert, Select, Space, Table, Tag, Typography } from "antd";
import { Link, useSearchParams } from "react-router-dom";
import { attentionApi } from "../api/attention";
import { errorMessage } from "../api/client";
import { useAuthStore } from "../auth/store";
import { PageHeader } from "../components/PageHeader";
import { LEVEL_TAG, dueText } from "./AttentionList";
import { objectLabel, targetPath } from "./attentionTarget";
import { DataTime } from "./widgetParts";
import "./desktop.css";

const SIZE = 25;

/**
 * ТЗ «Рабочие столы» 31.10.2026 (UXW-28/31, CW-20/25): «Показать все N» of «Требует внимания» — the bases of one
 * group (or of all) with the pages of the server. The kind and the page live in the address, so «Назад» from an
 * opened document returns to the same list; every row opens its own record.
 */
export function AttentionPage() {
  const [params, setParams] = useSearchParams();
  const kind = params.get("kind") || null;
  const page = Math.max(1, Number(params.get("page")) || 1);
  const user = useAuthStore((s) => s.me?.user.id ?? "-");
  const company = useAuthStore((s) => s.company?.id ?? "-");
  const query = useQuery({
    queryKey: ["dashboard", "attention", "page", user, company, kind ?? "", page],
    queryFn: () => attentionApi.get("staff", { kind, limit: SIZE, offset: (page - 1) * SIZE }),
    placeholderData: keepPreviousData,
    refetchInterval: 60000,
  });
  const data = query.data;
  const all = (data?.groups ?? []).reduce((sum, g) => sum + g.count, 0);
  const choose = (next: { kind?: string | null; page?: number }) => {
    const search = new URLSearchParams(params);
    const nextKind = next.kind === undefined ? kind : next.kind;
    if (nextKind) search.set("kind", nextKind);
    else search.delete("kind");
    const nextPage = next.page ?? 1;
    if (nextPage > 1) search.set("page", String(nextPage));
    else search.delete("page");
    setParams(search, { replace: true });
  };
  const group = data?.groups.find((g) => g.kind === kind);

  const columns = [
    {
      title: "Документ или объект",
      key: "object",
      render: (_: unknown, item: AttentionItem) => {
        const path = targetPath("staff", item.target);
        const label = objectLabel(item);
        return (
          <Space direction="vertical" size={0}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              <Tag color={LEVEL_TAG[item.level].color} style={{ marginRight: 6 }}>
                {LEVEL_TAG[item.level].word}
              </Tag>
              {item.object.type}
            </Typography.Text>
            {path ? (
              <Link to={path} className="wms-attention-number" data-testid={`attention-link-${item.id}`}>
                {label}
              </Link>
            ) : (
              <span className="wms-attention-number">{label}</span>
            )}
          </Space>
        );
      },
    },
    {
      title: "Что произошло",
      key: "reason",
      render: (_: unknown, item: AttentionItem) => {
        const basisPath = item.basis ? targetPath("staff", item.basis) : null;
        return (
          <>
            <div>{item.reason}</div>
            {item.basis && (
              <Typography.Text type="secondary">
                Основание: {basisPath ? <Link to={basisPath}>{item.basis.number}</Link> : item.basis.number}
              </Typography.Text>
            )}
          </>
        );
      },
    },
    { title: "Склад", key: "warehouse", render: (_: unknown, item: AttentionItem) => item.warehouse?.code ?? "" },
    { title: "Состояние", dataIndex: "status", key: "status" },
    { title: "Срок", key: "due", render: (_: unknown, item: AttentionItem) => dueText(item.due) },
    {
      title: "Что сделать",
      key: "next",
      render: (_: unknown, item: AttentionItem) => {
        const path = targetPath("staff", item.target);
        return path ? (
          <Link to={path} data-testid={`attention-open-${item.id}`}>
            {item.next_action}
          </Link>
        ) : (
          item.next_action
        );
      },
    },
  ];

  return (
    <div data-testid="attention-page">
      <PageHeader
        back
        title="Требует внимания"
        subtitle={
          data ? (
            <>
              {group ? `${group.label}: ${group.count}` : `Всего: ${all}`} · <DataTime at={data.generated_at} fetching={query.isFetching} />
            </>
          ) : undefined
        }
      />
      <Space wrap style={{ marginBottom: 12 }}>
        <div data-testid="attention-kind-filter">
          <Select
            style={{ minWidth: 280 }}
            value={kind ?? ""}
            onChange={(value) => choose({ kind: value || null })}
            options={[{ value: "", label: `Все предупреждения (${all})` }, ...(data?.groups ?? []).map((g) => ({ value: g.kind, label: `${g.label} (${g.count})` }))]}
            aria-label="Вид предупреждения"
          />
        </div>
      </Space>
      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Не удалось загрузить список"
          description={errorMessage(query.error)}
          action={kind ? <Link to="/attention">Показать все</Link> : undefined}
        />
      )}
      <div data-testid="attention-table">
        <Table<AttentionItem>
          rowKey="id"
          size="middle"
          loading={query.isPending || (query.isFetching && query.isPlaceholderData)}
          dataSource={data?.items ?? []}
          columns={columns}
          scroll={{ x: true }}
          locale={{ emptyText: "Всё спокойно: ничего не ждёт решения" }}
          pagination={{
            current: page,
            pageSize: SIZE,
            total: data?.total ?? 0,
            showSizeChanger: false,
            hideOnSinglePage: true,
            onChange: (next) => choose({ page: next }),
          }}
        />
      </div>
      <Typography.Paragraph type="secondary" style={{ marginTop: 12 }}>
        Переход не подтверждает, не проводит и не закрывает документ. Если запись уже недоступна, карточка скажет об этом и предложит вернуться на рабочий стол — обновите список.
      </Typography.Paragraph>
    </div>
  );
}
